import React, { useState, useEffect } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Settings,
  ChevronDown,
  ChevronUp,
  Trash2,
  LogOut,
  RefreshCw,
  Copy,
  Key,
  Zap,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

type CurrencyKey = 'EUR' | 'USD' | 'MXN' | 'BRL';

interface DevToolsPanelProps {
  onCurrencyChange?: (currency: CurrencyKey) => void;
}

export const DevToolsPanel: React.FC<DevToolsPanelProps> = ({
  onCurrencyChange,
}) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyKey>('EUR');
  const [isDevEnvironment, setIsDevEnvironment] = useState(false);
  const [isCurrencySectionOpen, setIsCurrencySectionOpen] = useState(false);
  const [isQuickActionsSectionOpen, setIsQuickActionsSectionOpen] =
    useState(false);
  const [isLanguageSectionOpen, setIsLanguageSectionOpen] = useState(false);
  const [isFeatureFlagsSectionOpen, setIsFeatureFlagsSectionOpen] =
    useState(false);
  const [isMockDataSectionOpen, setIsMockDataSectionOpen] = useState(false);
  const [isPerformanceSectionOpen, setIsPerformanceSectionOpen] =
    useState(false);
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({});
  const [performanceMetrics, setPerformanceMetrics] = useState({
    fps: 0,
    memory: 0,
    apiLatency: 0,
  });
  const [hasOverride, setHasOverride] = useState(false);
  const [copiedMessage, setCopiedMessage] = useState<string>('');
  const [hasMockData, setHasMockData] = useState(false);

  useEffect(() => {
    // SECURITY: Check if we're in development environment
    // This panel will NEVER render in production
    const hostname = window.location.hostname;
    const isDev =
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.includes('beta') ||
      hostname.includes('staging') ||
      hostname.includes('dev') ||
      process.env.NODE_ENV === 'development';

    setIsDevEnvironment(isDev);

    // Load saved currency from localStorage
    const savedCurrency = localStorage.getItem(
      'devtools_currency',
    ) as CurrencyKey;

    if (savedCurrency) {
      setSelectedCurrency(savedCurrency);
      setHasOverride(true);
      onCurrencyChange?.(savedCurrency);
    }

    // Load saved language from localStorage
    const savedLanguage = localStorage.getItem('devtools_language');
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [onCurrencyChange]);

  const handleCurrencyChange = (currency: CurrencyKey) => {
    setSelectedCurrency(currency);
    localStorage.setItem('devtools_currency', currency);
    setHasOverride(true);
    onCurrencyChange?.(currency);

    // Reload page to apply changes
    window.location.reload();
  };

  const clearOverride = () => {
    localStorage.removeItem('devtools_currency');
    setHasOverride(false);
    window.location.reload();
  };

  const handleClearAll = () => {
    // Clear currency override
    localStorage.removeItem('devtools_currency');
    setSelectedCurrency('EUR');
    setHasOverride(false);
    if (onCurrencyChange) {
      onCurrencyChange('EUR');
    }

    // Clear feature flags
    localStorage.removeItem('devtools_feature_flags');
    setFeatureFlags({});

    // Clear mock data
    localStorage.removeItem('mock_sales_data');
    setHasMockData(false);

    // Clear language override and restore to default
    localStorage.removeItem('devtools_language');
    i18n.changeLanguage('es'); // Default language

    showCopiedMessage('✅ All DevTools settings cleared!');
  };

  // Quick Actions handlers
  const handleClearCache = () => {
    localStorage.clear();
    sessionStorage.clear();
    showCopiedMessage('✅ Cache cleared!');
    setTimeout(() => window.location.reload(), 1000);
  };

  const handleForceLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/login';
  };

  const handleHardReload = () => {
    window.location.reload();
  };

  const handleCopyUserId = () => {
    const userId = localStorage.getItem('user_id') || 'No user ID found';
    navigator.clipboard.writeText(userId);
    showCopiedMessage('✅ User ID copied!');
  };

  const handleCopyAuthToken = () => {
    const token = localStorage.getItem('auth_token') || 'No token found';
    navigator.clipboard.writeText(token);
    showCopiedMessage('✅ Token copied!');
  };

  const showCopiedMessage = (message: string) => {
    setCopiedMessage(message);
    setTimeout(() => setCopiedMessage(''), 2000);
  };

  // Feature Flags handlers
  const toggleFeatureFlag = (key: string) => {
    const newFlags = { ...featureFlags, [key]: !featureFlags[key] };
    setFeatureFlags(newFlags);
    localStorage.setItem('devtools_feature_flags', JSON.stringify(newFlags));
    showCopiedMessage(`✅ ${key} ${newFlags[key] ? 'enabled' : 'disabled'}`);
  };

  // Mock Data generators
  const generateMockTips = (count: number) => {
    showCopiedMessage(`✅ Generated ${count} mock tips`);
    // TODO: Implement actual mock data generation
  };

  const generateMockSales = (count: number) => {
    // Generate realistic mock sales data
    const stores = ['STRIPE', 'PAYPAL'];
    const countries = [
      'ES',
      'MX',
      'FR',
      'US',
      'CO',
      'AR',
      'CL',
      'DO',
      'HN',
      'NO',
      'AU',
    ];
    const channelNames = [
      'STAKAZO DECLARACIONES 🗣️📣',
      'STAKAZO INFORMACIÓN TOP 💎',
      'MI STAKAZO MÁS GRANDE 💰',
      'STAKAZO ERROR DE CUOTA 🤫',
      'STAKE 100 ASIA v.3 🌏💰',
      'GRUPO PREMIUM OCTUBRE 💎',
    ];

    const mockSales = [];
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    // Get last day of current month to calculate days in month
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDayOfMonth.getDate();

    for (let i = 0; i < count; i++) {
      // Generate random day within current month
      const randomDay = Math.floor(Math.random() * daysInMonth) + 1;
      const createdAt = new Date(currentYear, currentMonth, randomDay);

      // Add random hours/minutes for variety
      createdAt.setHours(Math.floor(Math.random() * 24));
      createdAt.setMinutes(Math.floor(Math.random() * 60));

      const grossAmount = Math.floor(Math.random() * 2000) + 500; // 500-2500
      const feeAmount = Math.floor(grossAmount * 0.05); // ~5% fee

      mockSales.push({
        id: `mock_${Date.now()}_${i}`,
        type: 'PRODUCT_SALE',
        store: stores[Math.floor(Math.random() * stores.length)],
        tipster_id: 'c1o4edgmtfel9egorni0',
        country_code: countries[Math.floor(Math.random() * countries.length)],
        gross_income: {
          amount: grossAmount,
          currency: 'EUR',
          decimals: 2,
        },
        metadata: {
          product_name:
            channelNames[Math.floor(Math.random() * channelNames.length)],
          original_store_fee: {
            amount: feeAmount,
            currency: 'EUR',
            decimals: 2,
          },
          product_description: 'by BetData',
          store_fee_exchange_rate: 1,
        },
        created_at: createdAt.toISOString(),
      });
    }

    // Store in localStorage to simulate API response
    const existingSales = JSON.parse(
      localStorage.getItem('mock_sales_data') || '{"sales": []}',
    );
    const updatedSales = {
      sales_data: {
        summary: {
          total_sales: {
            amount: mockSales.reduce(
              (sum, s) => sum + s.gross_income.amount,
              0,
            ),
            currency: 'EUR',
            decimals: 2,
          },
        },
        sales: [...mockSales, ...(existingSales.sales_data?.sales || [])],
      },
    };

    localStorage.setItem('mock_sales_data', JSON.stringify(updatedSales));
    setHasMockData(true);
    showCopiedMessage(`✅ Generated ${count} mock sales`);

    // Trigger a page reload to show new data
    setTimeout(() => {
      window.location.reload();
    }, 1500);
  };

  const clearMockSales = () => {
    localStorage.removeItem('mock_sales_data');
    setHasMockData(false);
    showCopiedMessage('✅ Mock sales cleared!');
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  // Check if mock data exists on mount
  useEffect(() => {
    const mockData = localStorage.getItem('mock_sales_data');
    setHasMockData(!!mockData);
  }, []);

  // Performance monitoring
  useEffect(() => {
    if (!isPerformanceSectionOpen) return;

    const updateMetrics = () => {
      // FPS calculation
      let lastTime = performance.now();
      let frames = 0;
      const measureFPS = () => {
        frames++;
        const currentTime = performance.now();
        if (currentTime >= lastTime + 1000) {
          setPerformanceMetrics((prev) => ({ ...prev, fps: frames }));
          frames = 0;
          lastTime = currentTime;
        }
        requestAnimationFrame(measureFPS);
      };
      requestAnimationFrame(measureFPS);

      // Memory usage (if available)
      if ((performance as any).memory) {
        const memory = (performance as any).memory;
        const usedMemory = Math.round(memory.usedJSHeapSize / 1048576);
        setPerformanceMetrics((prev) => ({ ...prev, memory: usedMemory }));
      }
    };

    updateMetrics();
  }, [isPerformanceSectionOpen]);

  // Load saved feature flags
  useEffect(() => {
    const savedFlags = localStorage.getItem('devtools_feature_flags');
    if (savedFlags) {
      setFeatureFlags(JSON.parse(savedFlags));
    }

    // Restore saved language
    const savedLanguage = localStorage.getItem('devtools_language');
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  // Language switcher handler
  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('devtools_language', lang);
    showCopiedMessage(`✅ Language changed to ${lang.toUpperCase()}`);
  };

  // SECURITY: Don't render in production
  // This prevents the DevTools panel from appearing on production domains
  if (!isDevEnvironment) {
    return null;
  }

  const currencies: { key: CurrencyKey; label: string; symbol: string }[] = [
    { key: 'EUR', label: 'Euro', symbol: '€' },
    { key: 'USD', label: 'US Dollar', symbol: '$' },
    { key: 'MXN', label: 'Mexican Peso', symbol: '$' },
    { key: 'BRL', label: 'Brazilian Real', symbol: 'R$' },
  ];

  return (
    <div
      className={`fixed top-0 md:top-20 right-0 bottom-0 md:bottom-auto z-[9999] transition-transform duration-300 ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}
      style={{ maxWidth: '100vw' }}
    >
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="absolute -left-10 md:-left-12 top-24 flex h-10 w-10 md:h-12 md:w-12 items-center justify-center rounded-l-lg bg-gradient-to-br from-[#6633ff] to-[#8855ff] text-white shadow-lg hover:from-[#5522ee] hover:to-[#7744ff] transition-all"
        aria-label="Toggle DevTools"
      >
        {isOpen ? (
          <ChevronRight className="h-4 w-4 md:h-5 md:w-5" />
        ) : (
          <ChevronLeft className="h-4 w-4 md:h-5 md:w-5" />
        )}
      </button>

      {/* Panel Content */}
      <div className="h-full md:h-auto md:max-h-[calc(100vh-20rem)] md:rounded-l-xl w-[calc(100vw-45px)] md:w-[24rem] bg-[#1a1f26] shadow-2xl border-l border-white/10 flex flex-col">
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 md:space-y-6 scrollbar-hide">
          {/* Header */}
          <div className="flex items-center gap-3 border-b border-white/10 pb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-[#6633ff] to-[#8855ff]">
              <Settings className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">DevTools</h3>
              <p className="text-xs text-gray-400">Development Environment</p>
            </div>
          </div>

          {/* Currency Override Section */}
          <div className="space-y-3">
            <button
              onClick={() => setIsCurrencySectionOpen(!isCurrencySectionOpen)}
              className="flex w-full items-center justify-between rounded-lg p-3 transition-colors hover:bg-gray-100/5"
            >
              <div className="flex items-center gap-3">
                <label className="text-sm font-semibold text-white">
                  Currency Override
                </label>
                {hasOverride && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-500/20 px-2 py-0.5 text-xs font-bold text-green-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-green-400"></span>
                    ON
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {hasOverride && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      clearOverride();
                    }}
                    className="text-xs text-[#6633ff] hover:text-[#5522ee] font-medium transition-colors cursor-pointer"
                  >
                    Clear
                  </span>
                )}
                {isCurrencySectionOpen ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </div>
            </button>

            {isCurrencySectionOpen && (
              <>
                <div className="space-y-2 pl-3">
                  {currencies.map((currency) => (
                    <button
                      key={currency.key}
                      onClick={() => handleCurrencyChange(currency.key)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                        selectedCurrency === currency.key
                          ? 'bg-gradient-to-r from-[#6633ff] to-[#8855ff] text-white shadow-lg'
                          : 'bg-[#0f1318] text-gray-400 hover:bg-[#1a1f26] border border-white/10'
                      }`}
                    >
                      <span className="font-medium">{currency.label}</span>
                      <span className="text-sm font-mono">
                        {currency.symbol} {currency.key}
                      </span>
                    </button>
                  ))}
                </div>

                <p className="text-xs text-gray-500 mt-3 pl-3">
                  ⚠️ Changing currency will reload the page
                </p>
              </>
            )}
          </div>

          {/* Quick Actions Section */}
          <div className="space-y-3">
            <button
              onClick={() =>
                setIsQuickActionsSectionOpen(!isQuickActionsSectionOpen)
              }
              className="flex w-full items-center justify-between rounded-lg p-3 transition-colors hover:bg-gray-100/5"
            >
              <div className="flex items-center gap-3">
                <Zap className="h-4 w-4 text-yellow-400" />
                <label className="text-sm font-semibold text-white">
                  Quick Actions
                </label>
              </div>
              {isQuickActionsSectionOpen ? (
                <ChevronUp className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-400" />
              )}
            </button>

            {isQuickActionsSectionOpen && (
              <div className="grid grid-cols-2 gap-2 pl-3">
                <button
                  onClick={handleClearCache}
                  className="flex flex-col items-center gap-1.5 rounded-lg bg-[#0f1318] p-3 border border-white/10 hover:border-red-500/50 hover:bg-red-500/10 transition-all"
                >
                  <Trash2 className="h-4 w-4 text-red-400" />
                  <span className="text-xs font-medium text-white">
                    Clear Cache
                  </span>
                </button>

                <button
                  onClick={handleForceLogout}
                  className="flex flex-col items-center gap-1.5 rounded-lg bg-[#0f1318] p-3 border border-white/10 hover:border-red-500/50 hover:bg-red-500/10 transition-all"
                >
                  <LogOut className="h-4 w-4 text-red-400" />
                  <span className="text-xs font-medium text-white">Logout</span>
                </button>

                <button
                  onClick={handleHardReload}
                  className="flex flex-col items-center gap-1.5 rounded-lg bg-[#0f1318] p-3 border border-white/10 hover:border-[#6633ff]/50 hover:bg-[#6633ff]/10 transition-all"
                >
                  <RefreshCw className="h-4 w-4 text-[#6633ff]" />
                  <span className="text-xs font-medium text-white">Reload</span>
                </button>

                <button
                  onClick={handleCopyUserId}
                  className="flex flex-col items-center gap-1.5 rounded-lg bg-[#0f1318] p-3 border border-white/10 hover:border-[#6633ff]/50 hover:bg-[#6633ff]/10 transition-all"
                >
                  <Copy className="h-4 w-4 text-[#6633ff]" />
                  <span className="text-xs font-medium text-white">
                    User ID
                  </span>
                </button>

                <button
                  onClick={handleCopyAuthToken}
                  className="col-span-2 flex items-center justify-center gap-2 rounded-lg bg-[#0f1318] p-3 border border-white/10 hover:border-[#6633ff]/50 hover:bg-[#6633ff]/10 transition-all"
                >
                  <Key className="h-4 w-4 text-[#6633ff]" />
                  <span className="text-xs font-medium text-white">
                    Copy Auth Token
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* Language Switcher Section */}
          <div className="space-y-3">
            <button
              onClick={() => setIsLanguageSectionOpen(!isLanguageSectionOpen)}
              className="flex w-full items-center justify-between rounded-lg p-3 transition-colors hover:bg-gray-100/5"
            >
              <div className="flex items-center gap-3">
                <span className="text-lg">🌍</span>
                <label className="text-sm font-semibold text-white">
                  Language
                </label>
              </div>
              {isLanguageSectionOpen ? (
                <ChevronUp className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-400" />
              )}
            </button>

            {isLanguageSectionOpen && (
              <div className="grid grid-cols-2 gap-2 pl-3">
                <button
                  onClick={() => handleLanguageChange('en')}
                  className={`flex flex-col items-center gap-1.5 rounded-lg p-3 border transition-all ${
                    i18n.language === 'en'
                      ? 'bg-gradient-to-r from-[#6633ff] to-[#8855ff] border-[#6633ff] text-white'
                      : 'bg-[#0f1318] border-white/10 text-gray-400 hover:border-[#6633ff]/50 hover:bg-[#6633ff]/10'
                  }`}
                >
                  <span className="text-2xl">🇬🇧</span>
                  <span className="text-xs font-medium">English</span>
                </button>

                <button
                  onClick={() => handleLanguageChange('es')}
                  className={`flex flex-col items-center gap-1.5 rounded-lg p-3 border transition-all ${
                    i18n.language === 'es'
                      ? 'bg-gradient-to-r from-[#6633ff] to-[#8855ff] border-[#6633ff] text-white'
                      : 'bg-[#0f1318] border-white/10 text-gray-400 hover:border-[#6633ff]/50 hover:bg-[#6633ff]/10'
                  }`}
                >
                  <span className="text-2xl">🇪🇸</span>
                  <span className="text-xs font-medium">Español</span>
                </button>

                <button
                  onClick={() => handleLanguageChange('pt')}
                  className={`flex flex-col items-center gap-1.5 rounded-lg p-3 border transition-all ${
                    i18n.language === 'pt'
                      ? 'bg-gradient-to-r from-[#6633ff] to-[#8855ff] border-[#6633ff] text-white'
                      : 'bg-[#0f1318] border-white/10 text-gray-400 hover:border-[#6633ff]/50 hover:bg-[#6633ff]/10'
                  }`}
                >
                  <span className="text-2xl">🇧🇷</span>
                  <span className="text-xs font-medium">Português</span>
                </button>

                <button
                  onClick={() => handleLanguageChange('es-MX')}
                  className={`flex flex-col items-center gap-1.5 rounded-lg p-3 border transition-all ${
                    i18n.language === 'es-MX' || i18n.language === 'es_MX'
                      ? 'bg-gradient-to-r from-[#6633ff] to-[#8855ff] border-[#6633ff] text-white'
                      : 'bg-[#0f1318] border-white/10 text-gray-400 hover:border-[#6633ff]/50 hover:bg-[#6633ff]/10'
                  }`}
                >
                  <span className="text-2xl">🇲🇽</span>
                  <span className="text-xs font-medium">MX</span>
                </button>
              </div>
            )}
          </div>

          {/* Feature Flags Section */}
          <div className="space-y-3">
            <button
              onClick={() =>
                setIsFeatureFlagsSectionOpen(!isFeatureFlagsSectionOpen)
              }
              className="flex w-full items-center justify-between rounded-lg p-3 transition-colors hover:bg-gray-100/5"
            >
              <div className="flex items-center gap-3">
                <span className="text-lg">🐛</span>
                <label className="text-sm font-semibold text-white">
                  Feature Flags
                </label>
              </div>
              {isFeatureFlagsSectionOpen ? (
                <ChevronUp className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-400" />
              )}
            </button>

            {isFeatureFlagsSectionOpen && (
              <div className="space-y-2 pl-3">
                {[
                  { key: 'new_dashboard', label: 'New Dashboard Layout' },
                  { key: 'enhanced_editor', label: 'Enhanced Tip Editor' },
                  { key: 'real_time_notif', label: 'Real-time Notifications' },
                  { key: 'advanced_analytics', label: 'Advanced Analytics' },
                  { key: 'beta_payments', label: 'Beta Payment Methods' },
                ].map((flag) => (
                  <button
                    key={flag.key}
                    disabled={true}
                    onClick={() => toggleFeatureFlag(flag.key)}
                    className="disabled:opacity-50 hover:cursor-not-allowed w-full flex items-center justify-between px-4 py-2.5 rounded-lg bg-[#0f1318] border border-white/10 hover:border-[#6633ff]/50 transition-all"
                  >
                    <span className="text-sm text-white">{flag.label}</span>
                    <div
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        featureFlags[flag.key] ? 'bg-[#6633ff]' : 'bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          featureFlags[flag.key]
                            ? 'translate-x-5'
                            : 'translate-x-1'
                        }`}
                      />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Mock Data Section */}
          {/* <div className="space-y-3">
            <button
              onClick={() => setIsMockDataSectionOpen(!isMockDataSectionOpen)}
              className="flex w-full items-center justify-between rounded-lg p-3 transition-colors hover:bg-gray-100/5"
            >
              <div className="flex items-center gap-3">
                <span className="text-lg">{hasMockData ? '⚠️' : '📊'}</span>
                <label className="text-sm font-semibold text-white">
                  Mock Data
                </label>
                {hasMockData && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-yellow-500/20 px-2 py-0.5 text-xs font-bold text-yellow-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-yellow-400"></span>
                    ON
                  </span>
                )}
              </div>
              {isMockDataSectionOpen ? (
                <ChevronUp className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-400" />
              )}
            </button>

            {isMockDataSectionOpen && (
              <div className="space-y-2 pl-3">
                <div className="space-y-2">
                  <p className="text-xs text-gray-400 font-medium">
                    Generate Tips
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {[5, 10, 20].map((count) => (
                      <button
                        key={count}
                        onClick={() => generateMockTips(count)}
                        className="px-3 py-2 rounded-lg bg-[#0f1318] border border-white/10 hover:border-[#6633ff]/50 hover:bg-[#6633ff]/10 transition-all text-xs font-medium text-white"
                      >
                        {count} tips
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-xs text-gray-400 font-medium">
                    Generate Sales
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {[10, 50, 100].map((count) => (
                      <button
                        key={count}
                        onClick={() => generateMockSales(count)}
                        className="px-3 py-2 rounded-lg bg-[#0f1318] border border-white/10 hover:border-[#6633ff]/50 hover:bg-[#6633ff]/10 transition-all text-xs font-medium text-white"
                      >
                        {count} sales
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={clearMockSales}
                    className="w-full px-3 py-2 rounded-lg bg-[#0f1318] border border-red-500/50 hover:border-red-500 hover:bg-red-500/10 transition-all text-xs font-medium text-red-400"
                  >
                    🗑️ Clear Mock Sales
                  </button>
                </div>
              </div>
            )}
          </div> */}

          {/* Performance Monitor Section */}
          <div className="space-y-3">
            <button
              onClick={() =>
                setIsPerformanceSectionOpen(!isPerformanceSectionOpen)
              }
              className="flex w-full items-center justify-between rounded-lg p-3 transition-colors hover:bg-gray-100/5"
            >
              <div className="flex items-center gap-3">
                <span className="text-lg">🚀</span>
                <label className="text-sm font-semibold text-white">
                  Performance
                </label>
              </div>
              {isPerformanceSectionOpen ? (
                <ChevronUp className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-400" />
              )}
            </button>

            {isPerformanceSectionOpen && (
              <div className="space-y-2 pl-3">
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg bg-[#0f1318] border border-white/10 p-3">
                    <p className="text-xs text-gray-400 mb-1">FPS</p>
                    <p
                      className={`text-xl font-bold ${
                        performanceMetrics.fps >= 30
                          ? 'text-green-400'
                          : 'text-red-400'
                      }`}
                    >
                      {performanceMetrics.fps}
                    </p>
                  </div>

                  <div className="rounded-lg bg-[#0f1318] border border-white/10 p-3">
                    <p className="text-xs text-gray-400 mb-1">Memory</p>
                    <p
                      className={`text-xl font-bold ${
                        performanceMetrics.memory < 100
                          ? 'text-green-400'
                          : 'text-yellow-400'
                      }`}
                    >
                      {performanceMetrics.memory}
                      <span className="text-xs ml-1">MB</span>
                    </p>
                  </div>
                </div>

                <div className="rounded-lg bg-[#0f1318] border border-white/10 p-3">
                  <p className="text-xs text-gray-400 mb-1">API Latency</p>
                  <p className="text-xl font-bold text-blue-400">
                    {performanceMetrics.apiLatency}
                    <span className="text-xs ml-1">ms</span>
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Copied Message Toast */}
          {copiedMessage && (
            <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[10000] animate-fade-in">
              <div className="rounded-lg bg-green-500 px-4 py-2 shadow-lg">
                <p className="text-sm font-medium text-white">
                  {copiedMessage}
                </p>
              </div>
            </div>
          )}

          {/* Environment Info */}
          <div className="rounded-lg bg-[#0f1318] p-3 border border-white/10">
            <p className="text-xs text-gray-400 mb-1">Environment</p>
            <p className="text-sm font-mono text-white">
              {window.location.hostname}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
